import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { SubscriptionsAPI } from '../lib/api/subscriptions';
import { queryKeys } from '../lib/query/client';
import { AuthContext } from '../context/auth.context';
import { useCurrentOrg } from './useCurrentOrg';
import { ApiError } from '../lib/api/client';

/**
 * Hook to fetch and cache the current organization's subscription tier.
 * Only fetches when user is authenticated AND has selected an organization.
 *
 * A 402 "NO_SUBSCRIPTION" response is treated as a valid business state
 * (returns null data) rather than a query error. This prevents error toasts
 * and allows the sidebar/guards to handle the "no subscription" case cleanly.
 *
 * Subscriptions are an org-scoped concept, so the admin portal (which has no
 * AuthProvider) renders this hook as disabled rather than throwing.
 */
export function useSubscription() {
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const { currentOrg } = useCurrentOrg();

  return useQuery({
    queryKey: queryKeys.subscriptionCurrent(currentOrg?.id),
    queryFn: async () => {
      try {
        return await SubscriptionsAPI.getCurrentTier();
      } catch (error) {
        // 402 = no active subscription — this is a business state, not an error.
        // Return null so consumers see data=null instead of an error state.
        // Re-throw recoverable 402s so PostLoginRouter can attempt auto-reconcile.
        if (error instanceof ApiError && error.status === 402) {
          const detail = error.details as { detail?: { recoverable?: boolean } } | undefined;
          if (detail?.detail?.recoverable) {
            throw error;
          }
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - subscriptions don't change often
    enabled: isAuthenticated && !!currentOrg, // Only fetch when authenticated AND has org context
  });
}

/**
 * Hook to fetch and cache all available subscription tiers
 * Only fetches when user is authenticated AND has selected an organization
 */
export function useSubscriptionTiers() {
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const { currentOrg } = useCurrentOrg();

  return useQuery({
    queryKey: queryKeys.subscriptionTiers(),
    queryFn: () => SubscriptionsAPI.listTiers(),
    staleTime: 10 * 60 * 1000, // 10 minutes - tier list rarely changes
    enabled: isAuthenticated && !!currentOrg, // Only fetch when authenticated AND has org context
  });
}

/**
 * Helper hook to check if a specific feature is available in the current tier
 */
export function useFeatureAccess(featureKey: string): boolean {
  const { data: tier } = useSubscription();

  if (!tier) {
    return false; // Default to no access if tier not loaded
  }

  // Check if feature exists in the features object
  return tier.features?.[featureKey] === true;
}
